// src/api/campaignImages.ts
import { API_BASE_URL } from "../config";
import { getAdminToken } from "../auth";

type PresignResp = {
  uploads: { key: string; url: string }[];
};

function sleep(ms: number) {
  return new Promise((r) => setTimeout(r, ms));
}

async function putWithRetry(url: string, file: File, tries = 3): Promise<void> {
  let lastErr: any = null;

  for (let attempt = 1; attempt <= tries; attempt++) {
    try {
      const res = await fetch(url, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
        },
        body: file,
      });

      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(`R2 PUT failed: ${res.status} ${text}`);
      }
      return;
    } catch (e) {
      lastErr = e;
      await sleep(400 * attempt);
    }
  }

  throw lastErr;
}

/**
 * Upload campaign images directly to R2.
 * onProgress gets called as files complete: (done, total)
 */
export async function uploadCampaignImages(
  files: File[],
  onProgress?: (done: number, total: number) => void,
): Promise<string[]> {
  if (!files.length) return [];

  const token = getAdminToken();

  // 1) Presign
  const presignRes = await fetch(
    `${API_BASE_URL}/admin/campaign_uploads/presign`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      credentials: "include",
      body: JSON.stringify({
        files: files.map((f) => ({
          name: f.name,
          content_type: f.type || "application/octet-stream",
        })),
      }),
    },
  );

  if (!presignRes.ok) {
    const text = await presignRes.text().catch(() => "");
    throw new Error(`Presign failed: ${presignRes.status} ${text}`);
  }

  const presigned = (await presignRes.json()) as PresignResp;

  if (!presigned.uploads || presigned.uploads.length !== files.length) {
    throw new Error("Presign failed: invalid response");
  }

  // 2) Upload with limited concurrency + progress
  const CONCURRENCY = 4;
  const results: string[] = new Array(files.length);

  let done = 0;
  const total = files.length;
  onProgress?.(done, total);

  let i = 0;
  const workers = Array.from({ length: CONCURRENCY }, async () => {
    while (true) {
      const idx = i++;
      if (idx >= files.length) break;

      const file = files[idx];
      const { key, url } = presigned.uploads[idx];

      await putWithRetry(url, file, 3);

      results[idx] = `/${key}`;

      done += 1;
      onProgress?.(done, total);
    }
  });

  await Promise.all(workers);

  return results;
}
